import type { ExternalRequestStatus } from '@pm/schemas';
import {
  Badge,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Tabs,
} from '@pm/ui';
import { MailQuestion, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { fmtRelative } from '@/lib/format';
import { useExternalRequestCounts, useExternalRequests, useForms } from '@/lib/queries';
import { RequestStatusBadge } from '@/features/forms/form-bits';

type TabValue = 'open' | ExternalRequestStatus;

/** SPEC §32 — Inbox/Triagem: solicitações externas antes de virarem demanda (RULE-034). */
export function InboxPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const formId = params.get('form') ?? '';
  const [tab, setTab] = useState<TabValue>('open');
  const [q, setQ] = useState('');
  const { data: counts } = useExternalRequestCounts();
  const { data: forms = [] } = useForms();
  const status = tab === 'open' ? (['NEW', 'TRIAGE'] as ExternalRequestStatus[]) : [tab];
  const { data, isLoading, isError, error, refetch } = useExternalRequests({
    status,
    form_id: formId || undefined,
    q: q || undefined,
  });
  const rows = data?.data ?? [];

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        title="Triagem"
        subtitle="Solicitações recebidas pelos formulários. Aprovar cria a demanda com prioridade, responsável e prazo definidos aqui."
      />
      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            {
              value: 'open',
              label: 'Abertas',
              count: counts ? counts.NEW + counts.TRIAGE : undefined,
            },
            { value: 'NEW', label: 'Novas', count: counts?.NEW },
            { value: 'TRIAGE', label: 'Em triagem', count: counts?.TRIAGE },
            { value: 'APPROVED', label: 'Aprovadas', count: counts?.APPROVED },
            { value: 'REJECTED', label: 'Rejeitadas', count: counts?.REJECTED },
          ]}
        />
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-text-3" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Título, solicitante…"
              className="h-control-sm w-[220px] pl-7 text-sm"
            />
          </div>
          <Select
            value={formId}
            onChange={(e) => setParams(e.target.value ? { form: e.target.value } : {})}
            className="h-control-sm w-[200px] text-sm"
          >
            <option value="">Todos os formulários</option>
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={MailQuestion}
            title="Nada por aqui"
            description={
              tab === 'open'
                ? 'Nenhuma solicitação aguardando triagem.'
                : 'Nenhuma solicitação neste status.'
            }
          />
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong bg-bg font-mono text-xs uppercase tracking-[var(--tracking-caps)] text-text-3">
                <th className="h-8 px-3 text-left font-medium">Solicitação</th>
                <th className="h-8 px-3 text-left font-medium">Solicitante</th>
                <th className="h-8 px-3 text-left font-medium">Cliente</th>
                <th className="h-8 px-3 text-left font-medium">Formulário</th>
                <th className="h-8 px-3 text-left font-medium">Status</th>
                <th className="h-8 px-3 text-right font-medium">Recebida</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="h-row cursor-pointer border-b border-border last:border-b-0 hover:bg-surface-2"
                  onClick={() => navigate(`/triagem/${r.id}`)}
                >
                  <td className="max-w-[360px] truncate px-3">
                    <Link to={`/triagem/${r.id}`} className="font-medium hover:text-accent">
                      {r.title}
                    </Link>
                    {r.created_task ? (
                      <span className="ml-2 font-mono text-xs text-text-3">
                        → #{r.created_task.number}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 text-sm">
                    <span>{r.requester.name}</span>
                    {r.requester.contact ? (
                      <Badge tone="success" className="ml-2">
                        contato
                      </Badge>
                    ) : null}
                    <div className="font-mono text-xs text-text-3">{r.requester.email}</div>
                  </td>
                  <td className="px-3 text-sm text-text-2">
                    {r.client?.name ?? <span className="text-text-3">—</span>}
                  </td>
                  <td className="px-3 text-sm text-text-2">{r.form.name}</td>
                  <td className="px-3">
                    <RequestStatusBadge status={r.status} />
                  </td>
                  <td className="px-3 text-right font-mono text-xs text-text-3">
                    {fmtRelative(r.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
